
import { useToast } from '@/hooks/use-toast';
import { Lead } from '@/types/lead';
import { getWebSearchData, getWebSearchStatus, enrichLeadWithWebSearch } from '@/services/webSearchService';

export const useWebSearchRefresh = (
  lead: Lead,
  setSearchData: (data: any) => void,
  setSearchStatus: (status: any) => void,
  setProgressValue: (value: number) => void,
  setLastUpdated: (date: Date | null) => void,
  setQueuePosition: (position: number | null) => void,
  refreshInterval: number | null,
  setRefreshInterval: (interval: number | null) => void,
  calculateProgress: (status: any) => number,
  onDataUpdated?: () => void,
) => {
  const { toast } = useToast();

  const startStatusRefresh = () => {
    if (refreshInterval) {
      clearInterval(refreshInterval);
    }
    
    const interval = setInterval(async () => {
      try {
        const data = await getWebSearchData(lead.id);
        const liSource = data?.linkedIn?.dataSource;

        // Always store latest data so Company Insight can render progressively,
        // but DO NOT mark completed unless LinkedIn is verified.
        if (data) {
          setSearchData(data);
        }

        if (liSource === 'verified_linkedin') {
          setSearchStatus('completed');
          setProgressValue(calculateProgress('completed'));
          clearInterval(interval);
          setRefreshInterval(null);
          
          if (onDataUpdated) onDataUpdated();
          
          toast({
            title: "Web search completed",
            description: `Verified LinkedIn details fetched for ${lead.name}`,
          });
          return;
        }

        // No verified LinkedIn yet — keep polling, reflect server status
        const status = await getWebSearchStatus(lead.id);
        const currentStatus = status?.status || null;
        
        setSearchStatus(currentStatus);
        setProgressValue(calculateProgress(currentStatus));
        setLastUpdated(status?.updatedAt || null);
        
        if (currentStatus === 'pending') {
          const position = Math.floor(Math.random() * 5) + 1; // Simulate queue position
          setQueuePosition(position);
        } else {
          setQueuePosition(null);
        }
        
        if (currentStatus === 'failed') {
          clearInterval(interval);
          setRefreshInterval(null);
          
          toast({
            variant: "destructive",
            title: "Web search failed",
            description: "Unable to retrieve web information. Please try again later.",
          });
        }
      } catch (error) {
        console.error('Error refreshing status:', error);
      }
    }, 5000);
    
    setRefreshInterval(interval as unknown as number);
  };

  return { startStatusRefresh };
};
